import React, { useState, useEffect } from "react";
import { SetHeading } from "../../context/NavbarContext";
import { Breadcrumb, Button, Select } from "antd";
import { DoubleRightOutlined, DownloadOutlined } from "@ant-design/icons";
import ShimmerLoader from "../../loading";
import "../../styles.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { routes } from "../../Routes";
import LoadingSpinner from "../../components/Loading";
import {
  fetchAllVendors,
  getVendors,
} from "../../API  Functions/PurchaseFunctions";
import SearchBox from "../../components/CustomWidgets/SearchBox";
import { debounce } from "@mui/material";
import DateFilter from "../../components/ui/DateFilter";
import {
  cashInOutReport,
  getCreditHistory,
} from "../../API  Functions/SessionHistoryFunctions";
import CreditHistoryTable from "./CreditHistoryTable";
import CashiInOutTable from "./CashInOutTable";
import { getStoreUsers } from "../../API  Functions/UserMangement";
import ExportCasInOutReportModal from "./ExportCasInOutReportModal";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { toast } from "react-toastify";
import i18next from "i18next";
import AddNewExpense from "../../components/Setting/AddNewExpense";
import moment from "moment";
import { checkPermission } from "../../Utils/permissionHelper";
import dayjs from "dayjs";

const CashiInOutReport = () => {
  const { t, i18n } = useTranslation();
  const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
  const [loading, setloading] = useState(false);
  const { Option } = Select;
  
  const today = dayjs();
  const formatToday = today.format("YYYY-MM-DD");
  const startofMonth = today.startOf("month").format("YYYY-MM-DD");
  
  const [startDate, setStartDate] = useState(startofMonth);
  const [endDate, setEndDate] = useState(formatToday);
  const [selectedRange, setSelectedRange] = useState("thisMonth"); // Matched AntD/DateFilter naming standard

  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id
  );
  const isGinne = useSelector(
    (state) => state?.geneLoginReducer?.isGinneLogin
  );

  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store
  );

  const [currentLimit, setCurrentLimit] = useState(50); // Default limit
  const [currentOffset, setCurrentOffset] = useState(0); // Default offset
  const [query, setquery] = useState("");
  const [cashiInOutData, setCashiInOutData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [transactionModal, setOpenTransactionModal] = useState(false);

  // ⭐ FETCH INITIAL SYSTEM USERS
  useEffect(() => {
    GetUserData();
  }, []);

  // ⭐ CORE REACTIVE TRIGGER: Fetches data whenever pagination, user filters, or date ranges change
  useEffect(() => {
    GetCashInOutReport(selectedUser, query, currentLimit, currentOffset, startDate, endDate);
  }, [currentLimit, currentOffset, selectedUser, startDate, endDate]);

  const GetUserData = async () => {
    try {
      const res1 = await getStoreUsers(user_id);
      if (res1?.result[0]?.status_code === 200) {
        const activeUsers = res1.result[0]?.response?.filter(
          (user) => user.active === true
        );
        setUserData(activeUsers);
      } else {
        setUserData([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // ⭐ UPDATED API REPORT CALL WITH DATE LIMITS
  const GetCashInOutReport = async (user, text, limit, offset, start, end) => {
    setloading(true);
    const payload = {
      offset: offset,
      limit: limit,
      search: text,
      start_date: start || startDate, // 🔥 Connect start range
      end_date: end || endDate,     // 🔥 Connect end range
      ...(user && { user_id: user }),
    };
    try {
      const res1 = await cashInOutReport(payload);
      if (res1?.result?.status_code == 200) {
        setCashiInOutData(res1?.result?.response);
      } else {
        setCashiInOutData([]);
      }
    } catch (error) {
      console.error("Error fetching Cash In/Out report:", error);
      setCashiInOutData([]);
    } finally {
      setloading(false);
    }
  };

  const handleOptionChange = (selectedValue) => {
    setCurrentLimit(50);
    setCurrentOffset(0);
    if (selectedValue === "All Users") {
      setSelectedUser(null);
    } else {
      setSelectedUser(selectedValue);
    }
  };

  const debouncedSearch = debounce((value) => {
    setquery(value);
    setCurrentLimit(50);
    setCurrentOffset(0);
    GetCashInOutReport(selectedUser, value, 50, 0, startDate, endDate);
  }, 700);

  // ⭐ EXPORT SYSTEM WITH THE DATES
  const handleExport = async (dates) => {
    let payload = {
      lang: i18next.language === "ar" ? "ar_001" : "en_US",
      company_id: company_id,
      start_date: dates?.startDate || startDate,
      end_date: dates?.endDate || endDate,
      search: query || "",
      ...(selectedUser && { user_id: selectedUser }),
    };

    try {
      const response = await axiosInstance.post(URLS.POS_CASH_IN_OUT_EXCEL, payload);
      if (response?.data?.result?.status_code == 200) {
        downloadFile(response?.data?.result?.file);
      } else {
        toast.error(response?.data?.result?.status || t("Export failed"));
      }
    } catch (error) {
      console.error("Cash In/Out export error:", error);
      toast.error(t("Something went wrong while exporting"));
    }
  };

  const downloadFile = (base64Data) => {
    try {
      if (!base64Data || typeof base64Data !== "string" || base64Data.trim() === "") {
        alert("No valid file data available for download");
        return;
      }
      let cleanBase64 = base64Data
        .trim()
        .replace(/^data:[\w/+-]+;base64,/, "")
        .replace(/^["']|["']$/g, "")
        .replace(/\s/g, "");

      const padLength = 4 - (cleanBase64.length % 4);
      if (padLength < 4) cleanBase64 += "=".repeat(padLength);

      let binaryString = atob(cleanBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const header = new Uint8Array(bytes.buffer, 0, 8);
      let mimeType = "application/octet-stream";
      let fileExtension = "bin";

      if (header[0] === 0x50 && header[1] === 0x4b) {
        mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        fileExtension = "xlsx";
      } else if (header[0] === 0xd0 && header[1] === 0xcf && header[2] === 0x11 && header[3] === 0xe0) {
        mimeType = "application/vnd.ms-excel";
        fileExtension = "xls";
      }

      const blob = new Blob([bytes], { type: mimeType });
      const timestamp = moment().format('YYYY-MM-DD_HH.mm.ss');
      const filename = `${t('cash_in_out')}_${timestamp}.${fileExtension}`;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("File download failed:", error);
    }
  };

  return (
    <>
      <div className="flex w-full flex-col overflow-y-auto bg-[#F6F7F9] pb-2 md:h-full">
        <SetHeading value="Cash In/Out Report" />
        <div className="custom-scrollbar mx-2 flex-grow overflow-y-auto overflow-hidden md:ms-3 sm:mx-0 ">
          <div className="mt-6">
            <div className="me-1 flex flex-col gap-3 sm:me-3 md:me-8 md:flex-row md:items-start md:justify-between">
              <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row items-center">
                <SearchBox
                  searchResult={(values) => debouncedSearch(values)}
                  placeholder={t("Search Session/Cashier")}
                />
                <div className="w-full flex flex-col sm:flex-row gap-2 md:mx-3 md:w-auto items-center">
                  <Select
                    className="w-full overflow-hidden"
                    style={{
                      width: "100%",
                      maxWidth: 250,
                      minWidth: 200,
                      height: 40,
                    }}
                    placeholder={t("Select User")}
                    value={selectedUser || "All Users"}
                    onChange={handleOptionChange}
                  >
                    <Option value="All Users">{t("All Users")}</Option>
                    {Object.values(userData)?.map((option) => (
                      <Option key={option.user_id} value={option.user_id}>
                        {option.name} ({option.phone ? option.phone : option.email})
                      </Option>
                    ))}
                  </Select>

                  {/* ⭐ SYNCHRONIZED DATE FILTER */}
                  <DateFilter
                    setSelectedRange={setSelectedRange}
                    selectedRange={selectedRange}
                    SubmitFilter={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                      setCurrentLimit(50);
                      setCurrentOffset(0);
                      // Triggering via state dependencies in useEffect
                    }}
                  />
                </div>
              </div>
              <div className="flex w-full gap-3 md:w-auto md:justify-end">
                <button
                  className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primaryBlue text-sm font-semibold text-white sm:w-48"
                  onClick={() => {
                   handleExport()
                  }}
                >
                  <DownloadOutlined style={{ fontSize: "1.5em" }} />
                  <span>{t("Download in Excel")}</span>
                </button>
              </div>
            </div>

            {loading ? (
              <ShimmerLoader />
            ) : (
              <CashiInOutTable
                additionalData={cashiInOutData}
                pagination={(values) => {
                  setCurrentLimit(values.limit);
                  setCurrentOffset(values.offset);
                }}
              />
            )}
          </div>
        </div>

        {/* {exportModalOpen && (
          <ExportCasInOutReportModal
            heading={t("Download Cash In/Out Report")}
            isOpen={exportModalOpen}
            onClose={() => setExportModalOpen(false)}
            onExport={(dates) => handleExport(dates)}
            // Pass default states to modal initializer if needed
            defaultStartDate={startDate}
            defaultEndDate={endDate}
          />
        )} */}

        {transactionModal && (
          <AddNewExpense
            isOpen={transactionModal}
            onClose={() => setOpenTransactionModal(false)}
            userData={userData}
            ExpenseUpdated={() => {
              setOpenTransactionModal(false);
              setCurrentLimit(50);
              setCurrentOffset(0);
              setSelectedUser(null);
              setquery("");
              setStartDate(startofMonth);
              setEndDate(formatToday);
              setSelectedRange("thisMonth");
            }}
          />
        )}
      </div>
    </>
  );
};

export default CashiInOutReport;